import React, { FC, useEffect, useState } from "react";
import {
  Table,
  Descriptions,
  Tag,
  Empty,
  Collapse,
  Popover,
  Badge,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import ProjectPageShell, {
  useProjectContext,
} from "../shared/ProjectPageShell";
import { GenericStatusPill } from "../shared/statusColors";
import quotationApis from "../../../api/master/quotations";

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD-MMM-YYYY") : "-");
const fmtDateTime = (d?: string) =>
  d ? dayjs(d).format("DD-MMM-YYYY HH:mm") : "-";

const fmtAddress = (val: any): string => {
  if (val == null) return "-";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    const parts = [
      val.deliveryLocationName,
      val.addressLine1,
      val.addressLine2,
      val.cityName,
      val.districtName,
      val.stateName,
      val.countryName,
      val.pincode,
    ].filter((p) => p && String(p).trim().length > 0);
    return parts.length ? parts.join(", ") : "-";
  }
  return String(val);
};

const AddressPopover = ({ value }: { value: any }) => {
  if (!value || typeof value !== "object") {
    return <span>{value || "-"}</span>;
  }
  const short = value?.deliveryLocationName || value?.addressLine1 || "-";
  return (
    <Popover
      title="Address"
      content={
        <div style={{ maxWidth: 320, fontSize: 12 }}>
          {value.addressLine1 ? <div>{value.addressLine1}</div> : null}
          {value.addressLine2 ? <div>{value.addressLine2}</div> : null}
          <div>
            {[value.cityName, value.districtName, value.stateName, value.countryName]
              .filter(Boolean)
              .join(", ")}
          </div>
          {value.pincode ? <div>PIN: {value.pincode}</div> : null}
          {value.contactPersonName ? (
            <div style={{ marginTop: 6 }}>
              <strong>Contact:</strong> {value.contactPersonName}
              {value.mobileNumber ? ` (${value.mobileNumber})` : ""}
            </div>
          ) : null}
        </div>
      }
    >
      <span style={{ color: "#2557d6", cursor: "pointer", textDecoration: "underline" }}>
        {short}
      </span>
    </Popover>
  );
};

const ViewQuotation: FC = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  const navigate = useNavigate();
  const { projectName } = useProjectContext();

  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState<any>({});
  const [lines, setLines] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);

  useEffect(() => {
    if (quotationId) fetchQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const res: any = await quotationApis.getById(quotationId);
      const { response, data, status, message } = res || {};
      if (status) {
        const payload =
          (response?.quotationHeader ? response : null) ||
          (data?.quotationHeader ? data : null) ||
          response?.data ||
          data?.data ||
          response ||
          data ||
          {};
        setHeader(payload?.quotationHeader || {});
        setLines(payload?.quotationLines || []);
        setAttachments(payload?.quotationHeader?.dms || []);
        setSuppliers(payload?.suppliers || []);
      } else if (message) {
        Swal.fire("Error", message, "error");
      }
    } catch (err) {
      console.error("Failed to fetch quotation:", err);
    } finally {
      setLoading(false);
    }
  };

  const isLineLevelDelivery = header?.deliveryLevelType === "LINE_LEVEL";
  const isLineLevelDate = header?.deliveryDateType === "LINE_LEVEL";

  const lineColumns = [
    {
      title: "#",
      key: "lineNo",
      width: 50,
      render: (_: any, _r: any, idx: number) => idx + 1,
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 140,
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
    },
    {
      title: "Make",
      dataIndex: "makeInfo",
      key: "makeInfo",
      width: 140,
      render: (v: any, row: any) => {
        if (Array.isArray(v) && v.length > 0) {
          return v.map((m: any) => m.makeName).filter(Boolean).join(", ") || "-";
        }
        return row?.makeLabel || "-";
      },
    },
    {
      title: "Specification",
      dataIndex: "specification",
      key: "specification",
      width: 200,
    },
    {
      title: "QTY",
      dataIndex: "quantity",
      key: "quantity",
      width: 130,
      align: "right" as const,
      render: (v: any, row: any) => {
        const qty = v == null ? "-" : String(Number(v));
        const awarded = row?.awardedQty;
        if (awarded != null && awarded !== 0) {
          return (
            <span>
              {qty}{" "}
              <Popover
                content={<div>Awarded: {Number(awarded)}</div>}
                title="Awarded"
              >
                <Badge color="blue" />
              </Popover>
            </span>
          );
        }
        return qty;
      },
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 90,
    },
    ...(isLineLevelDate
      ? [
          {
            title: "Need By Date",
            dataIndex: "deliveryDate",
            key: "deliveryDate",
            width: 140,
            render: (v: string) => fmtDate(v),
          },
        ]
      : []),
  ];

  const supplierColumns = [
    {
      title: "#",
      key: "_idx",
      width: 50,
      render: (_: any, _r: any, idx: number) => idx + 1,
    },
    {
      title: "Supplier Name",
      dataIndex: "supplierName",
      key: "supplierName",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (s: string) => <GenericStatusPill status={s || "-"} />,
    },
    {
      title: "Bid Received",
      dataIndex: "bidReceived",
      key: "bidReceived",
      width: 130,
      render: (b: boolean) => (b ? "Yes" : "No"),
    },
    {
      title: "Bid Date",
      dataIndex: "bidReceivedDate",
      key: "bidReceivedDate",
      width: 140,
      render: (d: string) => fmtDate(d),
    },
  ];

  const headerStatus = header?.status || header?.actualStatus;

  const rfqLabel = `RFQ - ${header?.rfqId || quotationId || "-"}`;

  return (
    <ProjectPageShell
      headTitle="View Quotation"
      breadcrumb={[
        { label: projectName },
        { label: "Quotation" },
        { label: header?.rfqId || quotationId || "-" },
      ]}
      title={rfqLabel}
      eyebrow="Quotation"
      onBack={() => navigate(-1)}
      actions={
        <>
          {headerStatus ? <GenericStatusPill status={headerStatus} /> : null}
          {header?.version ? (
            <span style={{ marginLeft: 12 }}>Version {header.version}</span>
          ) : null}
          {header?.isSealedBid ? (
            <Tag color="purple" style={{ marginLeft: 8 }}>
              Sealed Bid
            </Tag>
          ) : null}
        </>
      }
      loading={loading}
    >
      <Collapse
                defaultActiveKey={["1", "2", "3"]}
                items={[
                  {
                    key: "1",
                    label: "General Information",
                    children: (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Descriptions.Item label="Quotation For">
                          {header?.quotationFor || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Preferred Brands">
                          {Array.isArray(header?.preferredBrands)
                            ? header.preferredBrands.join(", ") || "-"
                            : header?.preferredBrands || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Description">
                          {header?.quotationDescription || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Cost Center">
                          {header?.costCenterName || header?.costCenter || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last Submission Date">
                          {fmtDateTime(header?.lastSubmissionDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Time Limit">
                          {header?.deadlineType || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Site Name">
                          {header?.siteName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Contractor">
                          {header?.contractorName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Approvals">
                          {Array.isArray(header?.quotationApprovals) &&
                          header.quotationApprovals.length > 0
                            ? `${header.quotationApprovals.length} approver(s)`
                            : "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Additional Charges">
                          {header?.additionalCharges || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Auto Release">
                          {header?.autoRelease || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Pricing Mode">
                          {header?.pricingMode || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Billing Address">
                          <AddressPopover value={header?.billingAddressName} />
                        </Descriptions.Item>
                        <Descriptions.Item label="Delivery Address Type">
                          {header?.deliveryLevelType === "LINE_LEVEL"
                            ? "Line Level"
                            : header?.deliveryLevelType === "ORDER_LEVEL"
                            ? "Order Level"
                            : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Documents Required From Supplier">
                          {header?.supplierDocsRequired || "-"}
                        </Descriptions.Item>

                        {!isLineLevelDelivery ? (
                          <Descriptions.Item label="Delivery Address">
                            <AddressPopover value={header?.deliveryAddressName} />
                          </Descriptions.Item>
                        ) : null}
                        <Descriptions.Item label="Multiple Delivery Dates">
                          {isLineLevelDate ? "Yes" : "No"}
                        </Descriptions.Item>
                        {!isLineLevelDate ? (
                          <Descriptions.Item label="Need By Date">
                            {fmtDate(header?.deliveryDate)}
                          </Descriptions.Item>
                        ) : null}

                        <Descriptions.Item label="Payment Terms">
                          {header?.paymentTerms || "-"}
                        </Descriptions.Item>
                        {header?.paymentTerms === "Credit" ? (
                          <Descriptions.Item label="Credit Days">
                            {header?.expectedCreditDays ?? "-"}
                          </Descriptions.Item>
                        ) : null}
                        {header?.isSealedBid ? (
                          <Descriptions.Item label="Sealed Bid Submission">
                            {fmtDateTime(header?.sealedBidSubmissionDate)}
                          </Descriptions.Item>
                        ) : null}
                        {header?.isStrictEvaluationTimer ? (
                          <Descriptions.Item label="Evaluation Date">
                            {fmtDateTime(header?.evaluationDate)}
                          </Descriptions.Item>
                        ) : null}
                        {header?.postalAddress ? (
                          <Descriptions.Item label="Postal Address" span={3}>
                            {header.postalAddress}
                          </Descriptions.Item>
                        ) : null}
                      </Descriptions>
                    ),
                  },
                  {
                    key: "2",
                    label: `Material Information (${lines.length})`,
                    children: (
                      <Table
                        className="customTable pms-hub-table"
                        columns={lineColumns}
                        dataSource={lines}
                        rowKey={(r: any, i?: number) =>
                          String(r?.quotationLineId ?? i ?? Math.random())
                        }
                        pagination={false}
                        size="small"
                        scroll={{ x: 1200 }}
                        expandable={{
                          expandedRowRender: (record: any) => (
                            <div style={{ padding: "8px 12px" }}>
                              {record?.specification ? (
                                <div style={{ marginBottom: 6 }}>
                                  <strong>Specification:</strong>{" "}
                                  {record.specification}
                                </div>
                              ) : null}
                              {isLineLevelDelivery && record?.deliveryAddressName ? (
                                <div>
                                  <strong>Delivery Address:</strong>{" "}
                                  <AddressPopover
                                    value={record.deliveryAddressName}
                                  />
                                </div>
                              ) : null}
                              {Array.isArray(record?.dms) &&
                              record.dms.length > 0 ? (
                                <div style={{ marginTop: 6 }}>
                                  <strong>Attachments:</strong>{" "}
                                  {record.dms.map((d: any, i: number) => (
                                    <a
                                      key={d?.docId ?? i}
                                      href={d?.filePath}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ marginRight: 8 }}
                                    >
                                      {d?.fileName || d?.moduleFileType}
                                    </a>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ),
                          rowExpandable: (record: any) =>
                            !!record?.specification ||
                            (isLineLevelDelivery && !!record?.deliveryAddressName) ||
                            (Array.isArray(record?.dms) && record.dms.length > 0),
                        }}
                        locale={{ emptyText: <Empty description="No items" /> }}
                      />
                    ),
                  },
                  {
                    key: "3",
                    label: "Other Information",
                    children: (
                      <>
                        {suppliers.length > 0 && (
                          <>
                            <h6 style={{ fontWeight: 600, margin: "0 0 8px" }}>
                              Suppliers ({suppliers.length})
                            </h6>
                            <Table
                              className="customTable pms-hub-table"
                              columns={supplierColumns}
                              dataSource={suppliers}
                              rowKey={(r: any, i?: number) =>
                                String(r?.supplierId ?? i ?? Math.random())
                              }
                              pagination={false}
                              size="small"
                            />
                          </>
                        )}

                        {attachments.length > 0 && (
                          <>
                            <h6 style={{ fontWeight: 600, margin: "16px 0 8px" }}>
                              Attachments ({attachments.length})
                            </h6>
                            <ul style={{ paddingLeft: 18 }}>
                              {attachments.map((doc: any, idx: number) => (
                                <li key={doc?.docId ?? idx}>
                                  {doc?.filePath ? (
                                    <a
                                      href={doc.filePath}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {doc?.fileName ||
                                        doc?.moduleFileType ||
                                        "View"}
                                    </a>
                                  ) : (
                                    <span>{doc?.fileName || "-"}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                        {suppliers.length === 0 && attachments.length === 0 ? (
                          <Empty description="No additional information" />
                        ) : null}
                      </>
                    ),
                  },
                ]}
              />
    </ProjectPageShell>
  );
};

export default ViewQuotation;
